import type { ApprovalRequest } from '@/lib/types';
import { BucketShell } from './_BucketShell';
import { readDefaultFields, readStructuredFields } from './_payload';
import type { SupplierCandidate } from '../types';
import { SupplierSearchPicker, type SupplierHit } from '../SupplierSearchPicker';

export interface SupplierNotFoundViewProps {
  req: ApprovalRequest;
  onSupplierPicked?: (s: SupplierHit) => void;
}

function readSearchHits(req: ApprovalRequest): SupplierCandidate[] {
  const sf = readStructuredFields(req);
  const hits = sf['supplier_search_hits'];
  return Array.isArray(hits) ? (hits as SupplierCandidate[]) : [];
}

export function SupplierNotFoundView({ req, onSupplierPicked }: SupplierNotFoundViewProps) {
  const fields = readDefaultFields(req);
  const hits = readSearchHits(req);

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-orange-300 bg-orange-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          Supplier not found
        </p>
        <p className="mt-4 text-sm text-neutral-700">
          No supplier record exists for NTN{' '}
          <span className="font-mono text-neutral-900">{fields.ntn ?? '—'}</span> / vendor{' '}
          <span className="font-medium text-neutral-900">{fields.vendor ?? '—'}</span>.
        </p>
        <div className="mt-12">
          <p className="mb-6 text-xs font-medium uppercase tracking-wide text-neutral-500">
            Search Oracle suppliers
          </p>
          <SupplierSearchPicker
            onPick={(s) => {
              onSupplierPicked?.(s);
            }}
          />
          <p className="mt-8 text-xs text-neutral-500">
            Picking a supplier populates the reprocess form below; review and save to retry.
          </p>
        </div>
        {hits.length > 0 && (
          <div className="mt-12 rounded-md border border-orange-200 bg-neutral-0 p-12">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Loose matches (not auto-promoted)
            </p>
            <ul className="mt-8 space-y-4 text-sm">
              {hits.map((hit, idx) => (
                <li key={`${hit.supplier_id ?? idx}`} className="flex flex-wrap gap-x-12">
                  <span className="font-mono text-neutral-700">{hit.supplier_id ?? '-'}</span>
                  <span className="text-neutral-900">{hit.company_name ?? '-'}</span>
                  <span className="text-neutral-500">{hit.ntn_no ?? ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </BucketShell>
  );
}
